import React from 'react'
import * as yup from 'yup'

import { FormattedMessage } from '@divvy-web/i18n'

import { isForeignAddress, physicalStreet } from '@sputnik/schema/creditApplication/address'
import {
  dob,
  email,
  firstName,
  isNonUSCitizen,
  lastName,
  phoneNumber,
  ssn,
  title,
} from '@sputnik/schema/creditApplication/person'
import { requiredAddress } from '@sputnik/schema/creditApplication/requiredAddress'
import { maskedInput } from '@sputnik/schema/types'

const invalidPassportNumberErrorMessage = () => (
  <FormattedMessage
    defaultMessage='Please enter a valid passport number'
    id='sputnik.personSchema__cWenLy'
  />
)

const requiredPassportNumberErrorMessage = () => (
  <FormattedMessage
    defaultMessage='Passport number is required.'
    id='sputnik.addPersonSchema__cQPPLK'
  />
)

const passportNumber = maskedInput
  .when('isNonUSCitizen', {
    is: true,
    then: (schema) =>
      schema.test('isRequiredPassportNumber', requiredPassportNumberErrorMessage, (input, { path, createError }) => {
        if (!input || (!input.value && !input.display)) {
          return createError({
            path: `${path}.display`,
            message: requiredPassportNumberErrorMessage,
          })
        }

        return true
      }),
    otherwise: (schema) => schema,
  })
  .test('isValidPassportNumber', invalidPassportNumberErrorMessage, (input, { path, createError }) => {
    if (!input || !input.value) {
      return true
    }

    if (input.value.length < 8 || input.value.length > 9) {
      return createError({ path: `${path}.display`, message: invalidPassportNumberErrorMessage })
    }

    if (!input.value.match(/^[A-Za-z0-9*]*[0-9][A-Za-z0-9*]*$/)) {
      return createError({ path: `${path}.display`, message: invalidPassportNumberErrorMessage })
    }

    return true
  })
export const addPerson = requiredAddress.shape({
  street: physicalStreet.required(() => (
    <FormattedMessage
      defaultMessage='Street is required.'
      id='sputnik.addPersonSchema__gXPrkW'
    />
  )),
  streetAdditional: physicalStreet.nullable(),
  firstName: firstName.required(() => (
    <FormattedMessage
      defaultMessage='First name is required.'
      id='sputnik.addPersonSchema__lBU5Qr'
    />
  )),
  lastName: lastName.required(() => (
    <FormattedMessage
      defaultMessage='Last name is required.'
      id='sputnik.addPersonSchema__j7TwoG'
    />
  )),
  email: email.required(() => (
    <FormattedMessage
      defaultMessage='Email is required.'
      id='sputnik.addPersonSchema__yWTKH8'
    />
  )),
  phoneNumber: phoneNumber.required(() => (
    <FormattedMessage
      defaultMessage='Phone number is required.'
      id='sputnik.addPersonSchema__Il7p07'
    />
  )),
  dob: dob.required(() => (
    <FormattedMessage
      defaultMessage='Date of birth is required.'
      id='sputnik.addPersonSchema__BiACh2'
    />
  )),
  ssn: ssn,
  passportExpiration: yup
    .date()
    .min(new Date(), () => (
      <FormattedMessage
        defaultMessage='Enter an active passport'
        id='sputnik.personSchema__RtE2aX'
      />
    ))
    .typeError(() => (
      <FormattedMessage
        defaultMessage='Please enter a valid date format. For example, 01/31/1990'
        id='sputnik.addPersonSchema__kttXNz'
      />
    ))
    .nullable()
    .when('isNonUSCitizen', {
      is: true,
      then: (schema) =>
        schema.required(() => (
          <FormattedMessage
            defaultMessage='Passport expiration is required.'
            id='sputnik.addPersonSchema__MEoxqz'
          />
        )),
      otherwise: (schema) => schema,
    }),
  passportCountry: yup
    .string()
    .nullable()
    .when('isNonUSCitizen', {
      is: true,
      then: (schema) =>
        schema.required(() => (
          <FormattedMessage
            defaultMessage='Passport country is required.'
            id='sputnik.addPersonSchema__Cf+kdm'
          />
        )),
      otherwise: (schema) => schema,
    }),
  passportNumber: passportNumber,
})

export const addPersonForm = (isAuthSigner: boolean) =>
  addPerson.shape({
    isForeignAddress: isForeignAddress,
    isNonUSCitizen: isNonUSCitizen,
    title: title.when([], {
      is: () => isAuthSigner,
      then: (schema) =>
        schema.required(() => (
          <FormattedMessage
            defaultMessage='Title is required.'
            id='sputnik.addPersonSchema__jobTitleRequired'
          />
        )),
      otherwise: (schema) => schema,
    }),
  })

export const limitedAddPersonForm = addPerson.pick(['email', 'firstName', 'lastName'])

export type AddPersonForm = yup.InferType<ReturnType<typeof addPersonForm>>

export type LimitedAddPersonForm = yup.InferType<typeof limitedAddPersonForm>
